/**
 * Pure slot-expansion logic for the booking-calendar builder (Ad-hoc + Rolling), shared by the
 * drag interaction, the fill-from-availability-pattern pattern, and the preview. Framework-agnostic —
 * everything is in minutes-from-midnight within a single day; the UI maps pixels/dates to these.
 *
 * Ported from (and kept in step with) the validated interaction prototype. The behaviours here were
 * confirmed with the user; see the booking-calendar-overhaul notes.
 */

/** A time span within a day, in minutes from midnight. */
export interface MinuteSpan {
  start: number;
  end: number;
}

/** Half-open overlap test: [a1,a2) intersects [b1,b2). Touching edges do NOT overlap. */
export function overlaps(a1: number, a2: number, b1: number, b2: number): boolean {
  return a1 < b2 && b1 < a2;
}

/** Snap a minute value to the given granularity (e.g. 5 at default zoom, 1 when zoomed in). */
export function snapMinutes(min: number, granularity: number): number {
  return Math.round(min / granularity) * granularity;
}

/**
 * Precision (snap granularity, minutes) for a given row height. Coarser (5 min) at the default
 * zoom; finer (1 min) once the host zooms in enough to place a slot to the minute.
 */
export function precisionForRowHeight(rowHeightPx: number, fineThresholdPx = 34): number {
  return rowHeightPx >= fineThresholdPx ? 1 : 5;
}

/**
 * "Fill from availability pattern" over one day's run — the FIXED-GRID rule.
 *
 * Grid positions start at {@code from} and step by {@code duration + interval}. A position is
 * PLACED only if the whole {@code duration} block fits within {@code to} AND does not overlap any
 * {@code obstacle} (existing hand-drawn / manual slots). Colliding or non-fitting positions are
 * WASTED — the pattern never reflows or compresses to reclaim them. An interval tint follows a
 * placed block only when a later grid position exists (so no tint trails the run; a wasted block's
 * following tint is wasted too).
 *
 * Worked example: fillRun(540, 750, 60, 30, [{start:615,end:645}]) →
 *   blocks [{540,600}]  (09:00–10:00),  tints [{600,630}] (10:00–10:30);
 *   the 10:30 position collides (wasted) and 12:00 doesn't fit.
 */
export function fillRun(
  from: number,
  to: number,
  duration: number,
  interval: number,
  obstacles: MinuteSpan[],
): { blocks: MinuteSpan[]; tints: MinuteSpan[] } {
  const gap = Math.max(0, interval);
  const stride = duration + gap;
  const positions: number[] = [];
  for (let s = from; s + duration <= to + 1e-6; s += stride) positions.push(s);

  const blocks: MinuteSpan[] = [];
  const tints: MinuteSpan[] = [];
  positions.forEach((s, i) => {
    const e = s + duration;
    if (obstacles.some((o) => overlaps(s, e, o.start, o.end))) return; // collides → wasted
    blocks.push({ start: s, end: e });
    if (gap > 0 && i < positions.length - 1) tints.push({ start: e, end: e + gap });
  });
  return { blocks, tints };
}

/**
 * Hand-drawn slots never overlap each other: return {@code spans} with any that overlap {@code keep}
 * removed (the newest / just-edited slot wins). {@code keep} itself is preserved if present.
 */
export function withoutOverlapping<T extends MinuteSpan>(spans: T[], keep: MinuteSpan): T[] {
  return spans.filter((s) => s === keep || !overlaps(s.start, s.end, keep.start, keep.end));
}
